import { useEffect, useState } from 'preact/hooks';
import {
  getAdminToken,
  clearAdminTokens,
  platformAuth,
  PlatformUserSummary,
  adminCall,
} from './api';
import { LoginFlow } from './LoginFlow';
import { HomePage } from './HomePage';
import { AuditLogView } from './AuditLogView';
import { OrgsListPage } from './OrgsListPage';
import { OrgDetailPage } from './OrgDetailPage';
import { UsersSearchPage } from './UsersSearchPage';
import { NotificationsPage } from './NotificationsPage';
import { NotificationsConfigPage } from './NotificationsConfigPage';
import { PaymentEventsPage } from './PaymentEventsPage';
import { PlatformUsersPage } from './PlatformUsersPage';
import { BillingPage } from './BillingPage';
import { AgreementsPage } from './AgreementsPage';
import { ShortlinksPage } from './ShortlinksPage';
import { IntelligencePage, INTELLIGENCE_SERVICE } from './IntelligencePage';

type Route =
  | { kind: 'home' }
  | { kind: 'orgs' }
  | { kind: 'org'; id: string }
  | { kind: 'users' }
  | { kind: 'platform_users' }
  | { kind: 'billing' }
  | { kind: 'notifications' }
  | { kind: 'notifications_config' }
  | { kind: 'payments' }
  | { kind: 'audit' }
  | { kind: 'agreements' }
  | { kind: 'shortlinks' }
  | { kind: 'intelligence' };

export function AdminApp() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<PlatformUserSummary | null>(null);
  const [devBypass, setDevBypass] = useState(false);
  const [route, setRoute] = useState<Route>(parseRoute());
  const [intelligence, setIntelligence] = useState({ enabled: false, canClear: false });

  useEffect(() => {
    let current = true;
    setIntelligence({ enabled: false, canClear: false });
    if (user) adminCall(INTELLIGENCE_SERVICE, 'GetIntelligenceStatus')
      .then(s => { if (current) setIntelligence({ enabled: !!s.enabled, canClear: !!s.canClear }); })
      .catch(() => {});
    return () => { current = false; };
  }, [user]);

  useEffect(() => {
    refreshSession();

    const onExpired = () => {
      setUser(null);
      setLoading(false);
    };
    window.addEventListener('mp-admin-auth-expired', onExpired);
    const onPop = () => setRoute(parseRoute());
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('mp-admin-auth-expired', onExpired);
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  async function refreshSession() {
    if (!getAdminToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const resp = await platformAuth.whoAmI();
      setUser(resp.user);
    } catch {
      clearAdminTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    try {
      await platformAuth.logout();
    } catch {
      // Best effort.
    }
    clearAdminTokens();
    setUser(null);
    navigate('/');
  }

  function go(r: Route) {
    setRoute(r);
    navigate(routePath(r));
  }

  if (loading) {
    return <div class="admin-auth-shell"><div>Loading…</div></div>;
  }

  if (!user) {
    return (
      <LoginFlow
        onLoggedIn={(u, devBypassed) => {
          setUser(u);
          setDevBypass(devBypassed);
        }}
      />
    );
  }

  const navItems: Array<{ label: string; kind: Route['kind']; route: Route }> = [
    { label: 'Home', kind: 'home', route: { kind: 'home' } },
    { label: 'Organizations', kind: 'orgs', route: { kind: 'orgs' } },
    { label: 'Users', kind: 'users', route: { kind: 'users' } },
    { label: 'Platform users', kind: 'platform_users', route: { kind: 'platform_users' } },
    { label: 'Billing', kind: 'billing', route: { kind: 'billing' } },
    { label: 'Notifications', kind: 'notifications', route: { kind: 'notifications' } },
    { label: 'Notifications config', kind: 'notifications_config', route: { kind: 'notifications_config' } },
    { label: 'Payment events', kind: 'payments', route: { kind: 'payments' } },
    { label: 'Audit log', kind: 'audit', route: { kind: 'audit' } },
    { label: 'Agreements', kind: 'agreements', route: { kind: 'agreements' } },
    { label: 'Shortlinks', kind: 'shortlinks', route: { kind: 'shortlinks' } },
  ];
  if (intelligence.enabled) navItems.push({ label: 'Customer intelligence', kind: 'intelligence', route: { kind: 'intelligence' } });

  // When viewing an org, highlight "Organizations" in the sidebar.
  const activeKind = route.kind === 'org' ? 'orgs' : route.kind;

  return (
    <div class="admin-shell">
      {devBypass && (
        <div class="dev-banner">
          ⚠ 2FA bypass enabled (PLATFORM_2FA_REQUIRED=false). Dev mode only.
        </div>
      )}
      <header class="admin-header">
        <div class="brand">MomentPix · Platform Admin</div>
        <div class="spacer" />
        <div class="user">
          {user.name} ({user.username}) · {user.role}
        </div>
        <button onClick={logout}>Log out</button>
      </header>
      <aside class="admin-sidebar">
        {navItems.map((n) => (
          <a
            key={n.kind}
            href={routePath(n.route)}
            class={`nav-item ${activeKind === n.kind ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              go(n.route);
            }}
          >
            {n.label}
          </a>
        ))}
      </aside>
      <main class="admin-main">
        {route.kind === 'home' && <HomePage user={user} />}
        {route.kind === 'orgs' && <OrgsListPage onOpen={(id) => go({ kind: 'org', id })} />}
        {route.kind === 'org' && (
          <OrgDetailPage orgId={route.id} onBack={() => go({ kind: 'orgs' })} />
        )}
        {route.kind === 'users' && <UsersSearchPage />}
        {route.kind === 'platform_users' && <PlatformUsersPage />}
        {route.kind === 'billing' && <BillingPage />}
        {route.kind === 'notifications' && <NotificationsPage />}
        {route.kind === 'notifications_config' && <NotificationsConfigPage />}
        {route.kind === 'payments' && <PaymentEventsPage />}
        {route.kind === 'audit' && <AuditLogView />}
        {route.kind === 'agreements' && <AgreementsPage />}
        {route.kind === 'shortlinks' && <ShortlinksPage />}
        {route.kind === 'intelligence' && (intelligence.enabled ? <IntelligencePage canClear={intelligence.canClear} /> : <p>Customer intelligence is switched off.</p>)}
      </main>
    </div>
  );
}

function parseRoute(): Route {
  const p = window.location.pathname;
  if (p === '/' || p === '') return { kind: 'home' };
  if (p === '/orgs') return { kind: 'orgs' };
  const orgMatch = p.match(/^\/orgs\/([^/]+)$/);
  if (orgMatch) return { kind: 'org', id: orgMatch[1] };
  if (p === '/users') return { kind: 'users' };
  if (p === '/platform-users') return { kind: 'platform_users' };
  if (p === '/billing') return { kind: 'billing' };
  if (p === '/notifications') return { kind: 'notifications' };
  if (p === '/notifications/config') return { kind: 'notifications_config' };
  if (p === '/payments') return { kind: 'payments' };
  if (p === '/audit') return { kind: 'audit' };
  if (p === '/agreements') return { kind: 'agreements' };
  if (p === '/shortlinks') return { kind: 'shortlinks' };
  if (p === '/intelligence') return { kind: 'intelligence' };
  return { kind: 'home' };
}

function routePath(r: Route): string {
  switch (r.kind) {
    case 'home':
      return '/';
    case 'orgs':
      return '/orgs';
    case 'org':
      return `/orgs/${r.id}`;
    case 'users':
      return '/users';
    case 'platform_users':
      return '/platform-users';
    case 'billing':
      return '/billing';
    case 'notifications':
      return '/notifications';
    case 'notifications_config':
      return '/notifications/config';
    case 'payments':
      return '/payments';
    case 'audit':
      return '/audit';
    case 'agreements':
      return '/agreements';
    case 'shortlinks':
      return '/shortlinks';
    case 'intelligence':
      return '/intelligence';
  }
}

function navigate(path: string) {
  if (window.location.pathname !== path) {
    window.history.pushState({}, '', path);
  }
}
